/**
 * Smart product filter normalization.
 *
 * Different products often use different field names for the same concept
 * (e.g. "PN" vs "Tryckklass" vs "Pressure Class"). This module unifies them
 * into canonical filter groups and normalizes their values so B2B users
 * can filter intuitively.
 */

// Canonical filter groups. Each has a list of synonym field names the product
// variants might use, plus a value normalizer.
//
// IMPORTANT: Field-name matching is case- and whitespace-insensitive. The list
// below is the "canonical" form; matcher will normalize keys before lookup.
export const FILTER_GROUPS = {
  dimension: {
    key: 'dimension',
    label_sv: 'Dimension',
    label_en: 'Dimension',
    fields: [
      'dimension', 'storlek', 'dn', 'size', 'diameter',
      'anslutning', 'roranslutning', 'röranslutning', 'rörsanslutning',
      'connection', 'pipe connection',
    ],
    // Keep values mostly intact but uppercase the unit prefix
    normalize: (raw) => {
      const s = String(raw).trim();
      // Already in DN-form
      const m = s.match(/^DN\s*(\d+(?:[.,]\d+)?)/i);
      if (m) return `DN${m[1].replace(',', '.')}`;
      // "DN15 utvändigt gänga" → DN15
      const m2 = s.match(/DN\s*(\d+(?:[.,]\d+)?)/i);
      if (m2) return `DN${m2[1].replace(',', '.')}`;
      // Pure number → assume DN
      if (/^\d+(?:[.,]\d+)?\s*(mm)?$/i.test(s)) {
        return `DN${parseFloat(s.replace(',', '.'))}`;
      }
      // G-thread (G1, G 1, G3/4) → as-is
      if (/^G\s*\d/i.test(s)) {
        return s.replace(/\s+/g, '').toUpperCase();
      }
      return s;
    },
    sort: (a, b) => {
      const na = parseFloat((a.match(/\d+(?:[.,]\d+)?/) || ['0'])[0].replace(',', '.'));
      const nb = parseFloat((b.match(/\d+(?:[.,]\d+)?/) || ['0'])[0].replace(',', '.'));
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b, 'sv');
    },
  },
  material: {
    key: 'material',
    label_sv: 'Material',
    label_en: 'Material',
    fields: ['material', 'materialtyp', 'materialtype'],
    normalize: (raw) => {
      const s = String(raw).trim();
      const lower = s.toLowerCase();
      const map = {
        'stainless steel': 'Rostfritt stål',
        'rostfritt': 'Rostfritt stål',
        'rostfritt stål': 'Rostfritt stål',
        'carbon steel': 'Stål',
        'steel': 'Stål',
        'stål': 'Stål',
        'koppar': 'Koppar',
        'copper': 'Koppar',
        'brass': 'Mässing',
        'mässing': 'Mässing',
        'plast': 'Plast',
        'plastic': 'Plast',
      };
      return map[lower] || (s.charAt(0).toUpperCase() + s.slice(1));
    },
    sort: (a, b) => a.localeCompare(b, 'sv'),
  },
  pressureClass: {
    key: 'pressureClass',
    label_sv: 'Tryckklass',
    label_en: 'Pressure Class',
    fields: [
      'pn',
      'tryckklass', 'tryckklassen',
      'tryckklass (pn)', 'tryckklasspn',
      'pressure class', 'pressureclass', 'pressure_class',
      'pressure', 'tryck',
      'maximalt_driftstryck_pn', 'maximalt driftstryck pn', 'max driftstryck',
      'arbetstryck',
    ],
    normalize: (raw) => {
      const s = String(raw).trim();
      // Extract first numeric value, output as "PN<number>"
      const m = s.match(/(\d+(?:[.,]\d+)?)/);
      if (m) {
        const num = parseFloat(m[1].replace(',', '.'));
        return `PN${num % 1 === 0 ? num : num.toFixed(1)}`;
      }
      return s.toUpperCase();
    },
    sort: (a, b) => {
      const na = parseFloat((a.match(/\d+(?:[.,]\d+)?/) || ['0'])[0]);
      const nb = parseFloat((b.match(/\d+(?:[.,]\d+)?/) || ['0'])[0]);
      return na - nb;
    },
  },
};

// Normalize a field name for comparison (lowercase, trimmed, no spaces/symbols)
const normalizeKey = (k) => String(k || '').toLowerCase().trim().replace(/[\s_\-./()[\]]/g, '');

/**
 * For a given variant + group, returns the normalized value found in any
 * synonym field (or null if no match). Compares fields case- and
 * whitespace-insensitively so e.g. " Tryckklass" and "Tryckklass (PN)" both work.
 */
export const variantValueForGroup = (variant, group) => {
  if (!variant) return null;
  const groupKeys = new Set(group.fields.map(normalizeKey));
  for (const [rawKey, rawValue] of Object.entries(variant)) {
    if (rawValue == null || String(rawValue).trim() === '') continue;
    if (groupKeys.has(normalizeKey(rawKey))) {
      return group.normalize(rawValue);
    }
  }
  return null;
};

/**
 * Extract filter options + counts from all products.
 * Returns: { dimension: [{value, count}], material: [...], pressureClass: [...] }
 *
 * `currentFilters` object lets us compute "available" counts: e.g. if user has
 * already selected "Material: Stål", the dimension counts only reflect products
 * matching Stål. This produces the typical e-commerce facet behavior.
 */
export const buildFilterOptions = (products, currentFilters = {}) => {
  const result = {};

  for (const groupKey of Object.keys(FILTER_GROUPS)) {
    const group = FILTER_GROUPS[groupKey];
    // For this group, apply OTHER filters but not its own selection
    // (so user can see all options in the dimension they're toggling)
    const otherFilters = { ...currentFilters, [groupKey]: [] };

    const valueCounts = new Map();
    products.forEach((p) => {
      // Does this product match all OTHER active filter groups?
      const variants = p.variants || [];
      const productMatches = variants.some((v) => productVariantMatchesAll(v, otherFilters));
      if (!productMatches) return;

      // Collect distinct normalized values from variants in this group
      const seenInProduct = new Set();
      variants.forEach((v) => {
        const val = variantValueForGroup(v, group);
        if (val && !seenInProduct.has(val)) {
          seenInProduct.add(val);
          valueCounts.set(val, (valueCounts.get(val) || 0) + 1);
        }
      });
    });

    const sorted = [...valueCounts.entries()]
      .sort(([a], [b]) => group.sort(a, b))
      .map(([value, count]) => ({ value, count }));

    result[groupKey] = sorted;
  }

  return result;
};

/**
 * Does a single variant match ALL active filters?
 */
export const productVariantMatchesAll = (variant, filters) => {
  for (const groupKey of Object.keys(FILTER_GROUPS)) {
    const selected = filters[groupKey] || [];
    if (selected.length === 0) continue;
    const group = FILTER_GROUPS[groupKey];
    const value = variantValueForGroup(variant, group);
    if (!value || !selected.includes(value)) return false;
  }
  return true;
};

/**
 * Does a product match the active filters? (i.e. has at least one matching variant)
 */
export const productMatchesFilters = (product, filters) => {
  if (!filters || Object.values(filters).every((arr) => !arr || arr.length === 0)) return true;
  return (product.variants || []).some((v) => productVariantMatchesAll(v, filters));
};

/**
 * Generate human-readable label of active filters for SEO H1/title/meta.
 * E.g. "Stål · DN50 · PN10"
 */
export const activeFiltersToText = (filters) => {
  const parts = [];
  for (const groupKey of Object.keys(FILTER_GROUPS)) {
    (filters[groupKey] || []).forEach((v) => parts.push(v));
  }
  return parts.join(' · ');
};
